import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import { useTranslation } from "react-i18next";

const API_URL = import.meta.env.VITE_API_URL || "http://localhost:4001";

// Hàm map mã ngôn ngữ của client sang chuẩn backend
const mapLocaleForApi = (lng: string): string => {
  if (lng === "zh-Hant") return "zh-TW";
  if (lng === "zh") return "zh-CN";
  return lng || "en";
};

export interface Category {
  id: string;
  name: string;
  slug?: string;
}

export interface ProductApi {
  id: string;
  name: string;
  description?: string;
  grainLength?: string;
  packing?: string;
  moisture?: string;
  imageUrl?: string;
  categoryId: string;
  category?: { id: string; name: string };
}

export interface FormattedProduct {
  id: string;
  cat: string;
  name: string;
  desc: string;
  grade: string;
  packaging: string;
  moisture: string;
  img: string;
  categoryName: string;
}

export function useProductsData() {
  const { i18n } = useTranslation();
  const currentLocale = mapLocaleForApi(i18n.language);

  // 1. Fetch Danh mục theo ngôn ngữ
  const categoriesQuery = useQuery({
    queryKey: ["public-categories", currentLocale],
    queryFn: async () => {
      const res = await axios.get(`${API_URL}/api/categories`, {
        params: { locale: currentLocale },
      });
      return res.data;
    },
    staleTime: 1000 * 60 * 5,
  });

  // 2. Fetch Sản phẩm theo ngôn ngữ
  const productsQuery = useQuery({
    queryKey: ["public-products", currentLocale],
    queryFn: async () => {
      const res = await axios.get(`${API_URL}/api/products`, {
        params: { locale: currentLocale },
      });
      return res.data;
    },
    staleTime: 1000 * 60 * 5,
  });

  const rawCategories: Category[] =
    categoriesQuery.data?.data?.items || categoriesQuery.data?.data || [];
  const rawProducts: ProductApi[] =
    productsQuery.data?.data?.items || productsQuery.data?.data || [];

  const products: FormattedProduct[] = rawProducts.map((p) => ({
    id: p.id,
    cat: p.categoryId,
    name: p.name || "Unnamed Product",
    desc: p.description || "Vietnamese export rice standard quality.",
    grade: p.grainLength || "N/A",
    packaging: p.packing || "N/A",
    moisture: p.moisture || "N/A",
    img: p.imageUrl
      ? `${API_URL}${p.imageUrl}`
      : "https://via.placeholder.com/600x400?text=EverRice",
    categoryName: p.category?.name || "",
  }));

  return {
    categories: rawCategories,
    products,
    isLoading: categoriesQuery.isLoading || productsQuery.isLoading,
    isError: categoriesQuery.isError || productsQuery.isError,
  };
}
